import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Thermometer, Snowflake, Flame, Sun, Trash2, Loader2, Sparkles } from "lucide-react";

// Human-friendly body-part labels for the temperature-therapy dropdown. Keep
// this small and self-contained so it can be updated without touching the
// scheduling code that already uses its own list.
const BODY_PARTS = [
  { id: "neck", label: "Neck" },
  { id: "shoulder", label: "Shoulders" },
  { id: "upper_back", label: "Upper back" },
  { id: "lower_back", label: "Lower back" },
  { id: "wrist", label: "Wrists" },
  { id: "elbow", label: "Elbows" },
  { id: "hand", label: "Hands" },
  { id: "hip", label: "Hips" },
  { id: "knee", label: "Knees" },
  { id: "full_body", label: "Full body" },
];
const BODY_PART_LABEL = Object.fromEntries(BODY_PARTS.map((b) => [b.id, b.label]));

const TEMP_OPTIONS = [
  { id: "cool", label: "Cool",  Icon: Snowflake, color: "#2563eb", bg: "#dbeafe" },
  { id: "warm", label: "Warm",  Icon: Flame,     color: "#c2410c", bg: "#fed7aa" },
  { id: "both", label: "Both",  Icon: Sun,       color: "#7c3aed", bg: "#e9d5ff" },
];
const TEMP_LOOKUP = Object.fromEntries(TEMP_OPTIONS.map((t) => [t.id, t]));

function LogChip({ log, onRemove }) {
  const t = TEMP_LOOKUP[log.temperature] || TEMP_OPTIONS[0];
  const { Icon } = t;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold group"
      style={{ background: t.bg, color: t.color }}
      data-testid={`temp-log-chip-${log.log_id}`}
    >
      <Icon className="w-3 h-3" />
      {t.label} · {BODY_PART_LABEL[log.body_part] || log.body_part}
      <button
        type="button"
        onClick={() => onRemove(log.log_id)}
        aria-label="Remove"
        className="opacity-0 group-hover:opacity-100 transition -mr-1"
        data-testid={`temp-log-remove-${log.log_id}`}
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </span>
  );
}

/**
 * Per-day temperature-therapy control. Renders a checkbox to unlock the
 * inline form; once checked, the sonographer picks cool / warm / both plus a
 * body-part and saves. Recent selections surface as one-tap "preset" chips
 * above the form so future days can be logged without re-picking every field.
 */
export default function TemperatureTherapyPanel({ dateIso, disabled = false, onChanged }) {
  const [checked, setChecked] = useState(false);
  const [temp, setTemp] = useState("warm");
  const [part, setPart] = useState("shoulder");
  const [logs, setLogs] = useState([]);
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/therapy/temperature?start=${dateIso}&end=${dateIso}`);
      setLogs(data?.logs || []);
      // Pull broader presets (recent activity across all days) — separate call
      // is unnecessary because the endpoint already returns them.
      setPresets(data?.presets || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load temperature therapy.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateIso]);

  const save = async (overrides = {}) => {
    if (disabled) return;
    setBusy(true); setErr("");
    try {
      await api.post("/therapy/temperature", {
        date: dateIso,
        temperature: overrides.temperature || temp,
        body_part: overrides.body_part || part,
      });
      await load();
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/therapy/temperature/${id}`);
      await load();
      onChanged?.();
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white/70 p-2 space-y-2" data-testid={`temp-therapy-panel-${dateIso}`}>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={checked || logs.length > 0}
          disabled={disabled}
          onChange={(e) => setChecked(e.target.checked)}
          className="w-4 h-4 rounded border-zinc-300 accent-amber-500"
          data-testid={`temp-therapy-check-${dateIso}`}
        />
        <span className="text-[12px] font-bold text-zinc-800 flex items-center gap-1">
          <Thermometer className="w-3.5 h-3.5" style={{ color: "var(--gold-hover, #b5952f)" }} />
          Temperature Therapy
        </span>
        {logs.length > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: "var(--gold-light)", color: "var(--gold-hover)" }}>
            {logs.length}
          </span>
        )}
      </label>

      {/* Existing logs (chips) */}
      {logs.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`temp-therapy-logs-${dateIso}`}>
          {logs.map((l) => <LogChip key={l.log_id} log={l} onRemove={remove} />)}
        </div>
      )}

      {/* Form / quick-select — only visible when unlocked */}
      {(checked || logs.length > 0) && !disabled && (
        <div className="pt-1 space-y-2 border-t border-zinc-100">
          {presets.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-500 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Quick add
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {presets.map((p, i) => {
                  const t = TEMP_LOOKUP[p.temperature] || TEMP_OPTIONS[0];
                  const { Icon } = t;
                  return (
                    <button
                      key={`${p.temperature}-${p.body_part}-${i}`}
                      type="button"
                      disabled={busy}
                      onClick={() => save({ temperature: p.temperature, body_part: p.body_part })}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border transition hover:scale-[1.02]"
                      style={{ background: t.bg, color: t.color, borderColor: `${t.color}33` }}
                      data-testid={`temp-preset-${p.temperature}-${p.body_part}`}
                    >
                      <Icon className="w-3 h-3" />
                      {t.label} · {BODY_PART_LABEL[p.body_part] || p.body_part}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-1" role="radiogroup" aria-label="Temperature">
            {TEMP_OPTIONS.map((t) => {
              const { Icon } = t;
              const active = temp === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setTemp(t.id)}
                  className={`h-8 rounded-md text-[11px] font-bold border transition flex items-center justify-center gap-1 ${active ? "border-zinc-900" : "border-zinc-200"}`}
                  style={active ? { background: t.bg, color: t.color } : { background: "#fff", color: "#71717a" }}
                  data-testid={`temp-opt-${t.id}-${dateIso}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-1.5">
            <select
              value={part}
              onChange={(e) => setPart(e.target.value)}
              className="flex-1 h-8 rounded-md border border-zinc-200 px-2 text-[12px] bg-white"
              data-testid={`temp-part-${dateIso}`}
            >
              {BODY_PARTS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select>
            <button
              type="button"
              disabled={busy}
              onClick={() => save()}
              className="h-8 px-3 rounded-md text-[11px] font-bold text-white inline-flex items-center gap-1"
              style={{ background: "#0f172a" }}
              data-testid={`temp-save-${dateIso}`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </button>
          </div>

          {err && <div className="text-[11px] text-red-600" data-testid={`temp-err-${dateIso}`}>{err}</div>}
        </div>
      )}
    </div>
  );
}
